import {useCallback, useEffect, useRef, useSyncExternalStore} from 'react'

import {cookieStore, type CookieWriteOptions} from './cookie-storage-store'
import {validOrDefault, type Validator} from './validation'

const {cache, read, write, reset, subscribe} = cookieStore

interface CookieStorageOptions<T> extends CookieWriteOptions {
  /**
   * Type validation function or TypeBox-style validator object to validate read values. If an
   * encountered value fails validation, it will be silently discarded.
   */
  validator: Validator<T>
}

type UseCookieStorageResult<T> = [value: T, setValue: (value: T | ((prev: T) => T)) => void]

/** @deprecated Omitting `options.validator` is unsafe. Provide a `validator` in an `options` object as the third argument. */
export function useCookieStorage<T>(key: string, initialValue: T): UseCookieStorageResult<T>
/**
 * Cookie-persisted state with cross-component sync, built on
 * `useSyncExternalStore` so it's hydration-safe. Unlike local/sessionStorage,
 * cookies are sent on every request, so the server can render markup matching
 * the client's first paint — seed `initialValue` from the request cookie during
 * SSR to avoid a flash.
 */
export function useCookieStorage<T>(
  key: string,
  initialValue: T,
  options: CookieStorageOptions<T>,
): UseCookieStorageResult<T>
export function useCookieStorage<T>(
  key: string,
  initialValue: T,
  options?: CookieStorageOptions<T>,
): UseCookieStorageResult<T> {
  const initialValueRef = useRef(initialValue)
  useEffect(() => {
    initialValueRef.current = initialValue
  })

  // Keep options in a ref so `setValue`'s identity depends only on `key`, even
  // when callers pass an inline options literal.
  const optionsRef = useRef(options)
  useEffect(() => {
    optionsRef.current = options
  })

  const getSnapshot = useCallback((): T => {
    if (!cache.has(key)) {
      read(key, initialValue)
    }
    return validOrDefault(cache.get(key), initialValue, options?.validator)
  }, [key, initialValue, options?.validator])

  const getServerSnapshot = useCallback((): T => initialValue, [initialValue])

  const subscribeToStore = useCallback((listener: () => void) => subscribe(key, listener), [key])

  const value = useSyncExternalStore(subscribeToStore, getSnapshot, getServerSnapshot)

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      const current = cache.has(key)
        ? validOrDefault(cache.get(key), initialValueRef.current, optionsRef.current?.validator)
        : initialValueRef.current
      const resolved = typeof next === 'function' ? (next as (prev: T) => T)(current) : next
      // Treat undefined as a reset, never persist the literal "undefined".
      if (resolved === undefined) {
        reset(key, initialValueRef.current)
      } else {
        write(key, resolved, optionsRef.current)
      }
    },
    [key],
  )

  return [value, setValue]
}
